import { Box, Center, Flex, Icon, IconButton, Image, Stack, Text, useBreakpointValue } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaArrowRight, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { permissions, routes, sideBarPages } from "../../../config";
import { useAppConfig, useAuth } from "../../../context";
import { isAllowedToView, isModuleAvailable } from "utils/AuthUtils";
import * as React from "react";
import { Fragment, useState } from "react";
import IFRSLogo from "../../../assets/img/IFRS_logo.png";
import { organizationService } from "api/organization-administration";
import { useQuery, useQueryClient } from "react-query";

// TODO refactor this to reduce copy-paste and complexity of code for menu, sub-menu

/**
 * Left sidebar
 */
function Sidebar() {
  const { toggleSidebar, storedSidebarState, sidebarState } = useAppConfig();
  // const { userPermissions } = useAuth();
  const authContext = useAuth?.();
  const userPermissions = authContext ? authContext.userPermissions : [];
  const [openDropdown, setOpenDropdown] = useState(null);

  const sidebarContentW = useBreakpointValue({
    base: sidebarState ? "100vw" : 0,
    md: sidebarState ? "64" : "14",
    lg: storedSidebarState ? "64" : "14",
  });

  const sidebarTogglePosition = useBreakpointValue({
    base: sidebarState ? "100vw" : 0,
    md: sidebarState ? "60" : "10",
    lg: storedSidebarState ? "60" : "10",
  });

  const sidebarContentZIndex = useBreakpointValue({
    base: 10,
    md: 1,
  });

  const sidebarOpened = useBreakpointValue({
    base: sidebarState,
    lg: storedSidebarState,
  });

  return (
    <Flex
      id="sidebar"
      flexDir="column"
      inlineSize={sidebarContentW}
      blockSize="full"
      overflow="hidden"
      zIndex={sidebarContentZIndex}
      transitionTimingFunction="ease"
      transitionDuration="200ms"
      backgroundColor="darkTeal"
    >
      <SidebarHeader />
      <Box minHeight={sidebarOpened ? 5 : 3} marginBottom={sidebarOpened ? 3 : 0}>
        <IconButton
          aria-label={"toggle-button"}
          minWidth={sidebarOpened ? 10 : 7}
          height={sidebarOpened ? 10 : 7}
          fontSize={sidebarOpened ? "md" : "xs"}
          display={{ md: "inline-flex", base: "none" }}
          position="absolute"
          zIndex="20"
          left={sidebarTogglePosition}
          cursor="pointer"
          border="2px solid"
          borderColor="darkTeal"
          borderRadius="50%"
          color="darkTeal"
          backgroundColor="whiteShade.300"
          onClick={() => toggleSidebar()}
          _hover={{
            bg: "teal",
            color: "lime",
          }}
          icon={sidebarOpened ? <FaArrowLeft /> : <FaArrowRight />}
        />
      </Box>
      <Box flexGrow={1} overflowY="auto" overflowX="hidden">
        {sideBarPages
          // Check if the page is available for the current organization, only if it's associated to module
          .filter((menuItem) => isModuleAvailable(menuItem.module))
          .filter((menuItem) =>
            isAllowedToView(userPermissions, menuItem.linkProps.requiredPermissions, menuItem.linkProps.requiredModules)
          )
          .map((menuItem) => (
            <Box key={menuItem.name}>
                {menuItem.subMenus && menuItem.subMenus.length > 0 ? (
                  <SidebarItemWithSubMenus {...menuItem} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} />
                ) : (
                  <SidebarItem {...menuItem} />
                )}
            </Box>
          ))}
      </Box>
      <SidebarFooter userPermissions={userPermissions} />
    </Flex>
  );
}

function CollapseMenu({ mainItem, subItems, isActive, isOpen, onToggle, sideBarOpened }) {
  return (
    <Box cursor="pointer">
      <Flex
        onClick={onToggle}
        alignItems="center"
        fontWeight="semibold"
        _hover={{
          opacity: 1,
          bg: "teal",
        }}
      >
        {mainItem}
        <Box width="15%">
          {isOpen ? <FaChevronUp opacity={isActive ? 1 : 0.5} color="white" /> : <FaChevronDown color="white" />}
        </Box>
      </Flex>
      {isOpen && (
        <Box bg="whiteShade.700" margin={sideBarOpened ? "10px" : "2px"} padding="5px" borderRadius="8px">
          <Box borderLeft="1px solid" borderColor="whiteShade.600" margin={sideBarOpened ? "15px" : "0px"}>
            {subItems}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function SidebarItemWithSubMenus({
  name,
  mainRoute,
  linkProps: { icon, to },
  subMenus,
  openDropdown,
  setOpenDropdown,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const { storedSidebarState, sidebarState } = useAppConfig();
  const sidebarOpened = useBreakpointValue({
    base: sidebarState,
    lg: storedSidebarState,
  });

  const isParentItemActive =
    location.pathname.includes(mainRoute) && subMenus.some((sub) => sub.pathProps.path === location.pathname);

  const isOpen = openDropdown === name;

  const authContext = useAuth?.();
  const userPermissions = authContext ? authContext.userPermissions : [];

  const mainItem = (
    <Flex
      inlineSize={56}
      alignItems="center"
      fontSize="body.s"
      paddingInlineStart="4"
      paddingBlock="3"
      position="relative"
      fontWeight="semibold"
      width="100%"
      _hover={{
        opacity: 1,
        bg: "teal",
      }}
      title={sidebarOpened ? "" : t(`pages.${name}`)}
      color={isParentItemActive ? "red" : "whiteShade.300"}
    >
      <Icon fontSize="lg" marginInlineEnd="6" as={icon} color={isParentItemActive ? "lime" : "whiteShade.300"} />
      {sidebarOpened && (
        <Text fontSize="body.s" color={isParentItemActive ? "lime" : "whiteShade.300"} width={"70%"}>
          {t(`pages.${name}`)}
        </Text>
      )}
    </Flex>
  );

  const subItems = subMenus
    .filter((subMenu) =>
      isAllowedToView(userPermissions, subMenu.linkProps.requiredPermissions, subMenu.linkProps.requiredModules)
    )
    ?.map((subMenu) => (
      <Box key={subMenu.name}>
        <SidebarItem {...subMenu} hideIcon={true} />
      </Box>
    ));
  return (
    <Fragment>
      {to ? (
        <NavLink to={to}>{mainItem}</NavLink>
      ) : (
        <CollapseMenu
          mainItem={mainItem}
          subItems={subItems}
          isActive={isParentItemActive}
          isOpen={isOpen}
          onToggle={() => setOpenDropdown(isOpen ? null : name)}
          sideBarOpened={sidebarOpened}
        />
      )}
    </Fragment>
  );
}

function SidebarItem({
  name,
  linkProps: { icon, to, marginStart = 0, textFontSize = "body.s", iconFontSize = "lg" },
  hideIcon = false,
}) {
  const { t } = useTranslation();
  const { storedSidebarState, sidebarState } = useAppConfig();
  const sidebarOpened = useBreakpointValue({
    base: sidebarState,
    lg: storedSidebarState,
  });

  if (!to) {
    console.error("SidebarItem: to is not set for ", name);
    return (
      <Box>
        <Text>{name}</Text>
      </Box>
    );
  }

  return (
    <NavLink to={to}>
      {({ isActive }) => (
        <Flex
          inlineSize={56}
          alignItems="center"
          fontSize="body.s"
          paddingInlineStart="4"
          paddingBlock="2"
          position="relative"
          fontWeight="semibold"
          width="100%"
          _hover={{
            opacity: 1,
          }}
          title={sidebarOpened ? "" : t(`pages.${name}`)}
        >
          {(!sidebarOpened || !hideIcon) && (
            <Icon
              fontSize={iconFontSize}
              marginStart={marginStart}
              marginInlineEnd="6"
              as={icon}
              color={isActive ? "lime" : "whiteShade.300"}
            />
          )}
          {sidebarOpened && (
            <Text
              fontSize={textFontSize}
              marginStart={marginStart}
              color={isActive ? "lime" : "whiteShade.300"}
              width={"70%"}
            >
              {t(`pages.${name}`)}
            </Text>
          )}
        </Flex>
      )}
    </NavLink>
  );
}

function SidebarHeader() {

  // To fetch the organization data in order to get the logo and other organization details as necessary.
  const { user, getAccessTokenSilently } = useAuth();
  const org_id = user?.org_id;
  const { data: orgData } = useQuery({
    queryKey: ["org", org_id],
    queryFn: () => getAccessTokenSilently().then(() => organizationService.get(org_id)),
    enabled: !!org_id,
  });

  const navigate = useNavigate();
  // const { userPermissions } = useAuth();
  const authContext = useAuth?.();
  const userPermissions = authContext ? authContext.userPermissions : [];
  const subDomain = window.location.hostname.split(".")[0];
  let logoSrc;
  try {
    if(orgData?.logoUrl === null){
      // This is the old way of presenting the organization logo.(FALLBACK TO ACCOMODATE LATE MIGRATION).
      logoSrc = require(`../../../assets/company-logos/${subDomain}.png`);
    }else{
      // This is the new way of presenting the organization logo.
      logoSrc = orgData?.logoUrl;
    }
  } catch (error) {
    logoSrc = null;
  }

  return (
    <Box flexShrink={0} flexGrow={0} padding={3}>
      <Center>
        {logoSrc && (
          <Image
            paddingInlineEnd="2px"
            htmlWidth="100%"
            objectFit="fit"
            src={logoSrc}
            cursor="pointer"
            onClick={() => navigate(getDashboardRoute(userPermissions))}
          />
        )}
      </Center>
    </Box>
  );
}

function SidebarFooter({ userPermissions }) {
  const { storedSidebarState, sidebarState } = useAppConfig();
  const sidebarOpened = useBreakpointValue({
    base: sidebarState,
    lg: storedSidebarState,
  });

  return (
    <Flex flexGrow={0} flexShrink={0} paddingInline="2" paddingBlock="4" justifyContent="center">
      {sidebarOpened && <Image src={IFRSLogo} minHeight="40px" />}
    </Flex>
  );
}

const getDashboardRoute = (userPermissions) => {
  const dashboardRouteObjects = [
    { permissions: permissions.dashboard, route: routes.dashboard },
    { permissions: permissions.supplierDashboard, route: routes.supplierDashboard },
    {
      permissions: permissions.dataCollectionOrganisationQuestionnaires,
      route: routes.dataCollectionOrganisationQuestionnaires,
    },
  ];

  return dashboardRouteObjects.find((obj) => isAllowedToView(userPermissions, obj.permissions))?.route ?? "";
};

export default Sidebar;
